<template>
  <TheObserver
    :handle-intersect="handleIntersect"
    :handle-leave="handleLeave"
    :root-margin="`-${headerHeight}px 0px 0px 0px`"
  />
  <div
    class="left-0 z-page-navigation w-full bg-white"
    :class="[
      isFixed && 'shadow-header',
      !isDesktop && isShowMobileMenu ? 'fixed shadow-header' : 'sticky',
    ]"
    :style="{ top: `${headerHeight}px` }"
  >
    <div class="container-wrap">
      <div
        class="flex w-full items-center justify-between desktop:justify-end"
        :style="{ height: `${navigationHeight}px` }"
      >
        <nav
          v-if="isDesktop"
          class="relative w-full flex-1 overflow-hidden px-2.5"
        >
          <ul
            ref="scrollbarRef"
            class="scrollbar-h-px flex w-full items-center overflow-y-hidden overflow-x-scroll"
            :style="{ height: `${navigationHeight}px` }"
          >
            <li
              v-for="item in navigation"
              :key="item.id"
              :class="
                activeId === item.id ? 'border-blue-400' : 'border-transparent'
              "
              class="border-b-4 border-solid px-2.5"
            >
              <button
                class="block whitespace-nowrap py-1 text-sm font-bold hover:text-blue-400"
                :title="item.name"
                @click="handleScrollTo(item.id, offset)"
              >
                {{ item.name }}
              </button>
            </li>
            <slot name="items-side" />
          </ul>

          <button
            v-if="!isLeftMax"
            class="absolute left-2.5 top-0 flex w-[50px] items-center justify-center bg-white/50"
            :style="{ height: `${navigationHeight}px` }"
            @click="handleNavScroll('left')"
          >
            <IconCaret direction="left" class="text-blue-400" />
          </button>

          <button
            v-if="!isRightMax"
            class="absolute right-2.5 top-0 flex w-[50px] items-center justify-center bg-white/50"
            :style="{ height: `${navigationHeight}px` }"
            @click="handleNavScroll('right')"
          >
            <IconCaret direction="right" class="text-blue-400" />
          </button>
        </nav>

        <template v-else>
          <button class="h-6 w-6 basis-6" @click="emits('click-menu-button')">
            <IconClose v-if="isShowMobileMenu" class="h-full w-full" />
            <IconMenu v-else class="h-full w-full" />
          </button>
          <div class="flex-1 overflow-hidden pl-2">
            <p class="truncate font-bold">{{ triggerSectionName }}</p>
          </div>
        </template>

        <div class="flex items-center">
          <slot name="right-side" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useScroll, useWindowSize } from '@vueuse/core';

  interface IProps {
    triggerId: string;
    isShowMobileMenu: boolean;
    navigation: { id: string; name: string }[];
  }

  const props = withDefaults(defineProps<IProps>(), {
    triggerId: '',
    isShowMobileMenu: false,
    navigation: () => [],
  });

  const emits = defineEmits<{ (e: 'click-menu-button'): void }>();

  const { width } = useWindowSize();
  const { isDesktop } = useScreen();
  const { headerHeight, navigationHeight } = useWebsite();

  const scrollbarRef = ref<HTMLElement>();
  const isFixed = ref<boolean>(false);
  const isLeftMax = ref<boolean>(true);
  const isRightMax = ref<boolean>(true);

  const { x: scrollX, isScrolling } = useScroll(scrollbarRef, {
    behavior: 'smooth',
  });

  const offset = computed(
    (): number => headerHeight.value + navigationHeight.value
  );

  const { handleScrollTo } = useScrollTo({
    offset: offset.value,
  });

  const activeId = computed((): string => {
    if (props.triggerId) {
      return props.triggerId;
    }
    if (props.navigation.length > 0) {
      return props.navigation[0].id;
    }
    return '';
  });

  const triggerSectionName = computed((): string => {
    const triggerItem = props.navigation.find(
      (item) => item.id === activeId.value
    );
    return triggerItem ? triggerItem.name : '';
  });

  const handleLeave = (): void => {
    isFixed.value = true;
  };

  const handleIntersect = (): void => {
    isFixed.value = false;
  };

  const handleNavScroll = (direction: 'left' | 'right'): void => {
    if (!scrollbarRef.value) return;
    const scrollAmount =
      direction === 'right' ? scrollbarRef.value.scrollWidth : 0;
    scrollX.value = scrollAmount;
  };

  const checkHasScrollArrow = (): void => {
    nextTick(() => {
      if (scrollbarRef.value) {
        isLeftMax.value = scrollbarRef.value.scrollLeft <= 10;
        isRightMax.value =
          scrollbarRef.value.scrollLeft >=
          scrollbarRef.value.scrollWidth - scrollbarRef.value.clientWidth - 10;
      }
    });
  };

  watch(width, () => checkHasScrollArrow());

  watch(isDesktop, () => checkHasScrollArrow());

  watch(
    () => props.navigation,
    () => checkHasScrollArrow(),
    { deep: true }
  );

  watch(isScrolling, (value: boolean) => {
    if (!value) {
      checkHasScrollArrow();
    }
  });

  onMounted(() => {
    checkHasScrollArrow();
  });
</script>
